/**
 * Module-level cache of `Intl.NumberFormat` instances.
 *
 * WHY THIS EXISTS
 * ---------------
 * Constructing an `Intl.NumberFormat` is expensive; calling `.format()` on an
 * existing one is not. Measured on Node 24.19.0:
 *
 *   construct + format .................. 13,839 ns
 *   format() on an existing instance ........ 227 ns
 *
 * `formatDollars`, `formatNumber` and `$formatPercentage` each constructed a
 * fresh one **per call**, and they run once per rendered table cell — so a
 * currency column paid ~13.8µs a cell, on every sort, search keystroke and
 * re-render. Sharing the instances is safe: `.format()` is pure, and an
 * instance holds no per-call state.
 *
 * TWO LOOKUP PATHS, AND WHY
 * -------------------------
 * 1. **Identity** (`WeakMap` on the options object) — free, measured 223 ns
 *    end-to-end, i.e. indistinguishable from calling `.format()` directly.
 *    This is the path callers get when they pass a module-level constant, which
 *    is why the callers in this repo define their options at module scope.
 * 2. **Value** (`Map` on a `locale|JSON.stringify(options)` key) — 556 ns, so
 *    still 25x better than constructing. It exists so that a caller passing a
 *    fresh object literal per call gets caching anyway. Without it, such a
 *    caller would silently fall back to 13.8µs with nothing to show for it —
 *    the kind of invisible regression this codebase has been bitten by before.
 *
 * The value key is order-sensitive (`{a,b}` and `{b,a}` produce two entries)
 * and drops `undefined` values. Both are harmless: the worst case is one extra
 * cached instance, never a wrong format, because `{notation: undefined}` and
 * `{}` genuinely mean the same thing to `Intl.NumberFormat`.
 */

/** Fast path: the same options OBJECT, per locale. */
const byOptionsIdentity = new WeakMap<
  Intl.NumberFormatOptions,
  Map<string, Intl.NumberFormat>
>()

/** Fallback path: value-equal options arriving as a fresh object. */
const byOptionsValue = new Map<string, Intl.NumberFormat>()

/**
 * Bound on the fallback cache. Every caller here uses a fixed set of options,
 * so in practice it holds a handful of entries forever — this only matters if
 * options are ever derived from data (a per-row currency, say), where an
 * unbounded Map would be a slow memory leak. Clearing wholesale rather than
 * evicting an entry keeps it to one branch; the cost of a rare rebuild is one
 * construction per live options set.
 */
const MAX_VALUE_CACHE_ENTRIES = 64

/**
 * An `Intl.NumberFormat` for this locale + options, reused across calls.
 * Pass a module-level constant for `options` to get the free lookup path.
 */
export function getNumberFormat(
  locale: string,
  options: Intl.NumberFormatOptions
): Intl.NumberFormat {
  let byLocale = byOptionsIdentity.get(options)
  const byIdentity = byLocale?.get(locale)
  if (byIdentity) {
    return byIdentity
  }

  const key = `${locale}|${JSON.stringify(options)}`
  let formatter = byOptionsValue.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options)
    if (byOptionsValue.size >= MAX_VALUE_CACHE_ENTRIES) {
      byOptionsValue.clear()
    }
    byOptionsValue.set(key, formatter)
  }

  // Promote to the identity path so a constant options object pays the string
  // key exactly once, on its first call.
  if (!byLocale) {
    byLocale = new Map()
    byOptionsIdentity.set(options, byLocale)
  }
  byLocale.set(locale, formatter)

  return formatter
}

/** Size of the fallback cache. Exists for the spec that guards the bound. */
export function getNumberFormatCacheSize(): number {
  return byOptionsValue.size
}
